export interface OrderableUser {
  id: string | number;
  fullName: string;
  isWhitelisted: boolean;
}

export interface RowOrderState {
  signature: string;
  ranks: Map<string, number>;
}

export const createRowOrderState = (): RowOrderState => ({
  signature: "",
  ranks: new Map(),
});

/**
 * Orders users for the Users grid while keeping row positions stable.
 *
 * Ranking (licensed first, then alphabetical) is recomputed only when the set
 * of visible people changes - a fetch, a search, or a filter. Toggling a
 * licence deliberately does NOT re-rank: re-sorting on every toggle made rows
 * jump out from under the cursor, so the next click landed on a different
 * person or re-toggled the same one.
 *
 * `state` is mutated in place to carry the frozen order across calls.
 */
export const orderUsersStably = <T extends OrderableUser>(
  users: T[],
  state: RowOrderState
): T[] => {
  const signature = users
    .map((user) => user.id.toString())
    .sort()
    .join(",");

  if (state.signature !== signature) {
    const ranks = new Map<string, number>();
    [...users]
      .sort((a, b) => {
        if (a.isWhitelisted !== b.isWhitelisted) return a.isWhitelisted ? -1 : 1;
        return a.fullName.localeCompare(b.fullName);
      })
      .forEach((user, index) => ranks.set(user.id.toString(), index));
    state.signature = signature;
    state.ranks = ranks;
  }

  const { ranks } = state;
  return [...users].sort(
    (a, b) => (ranks.get(a.id.toString()) ?? 0) - (ranks.get(b.id.toString()) ?? 0)
  );
};
